import { Smartphone, LogOut, Apple, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { useTenant } from '@/hooks/useTenant';

// Store links: placeholders until the Steddy technician app is published.
const APP_STORE_URL = 'https://apps.apple.com/';
const PLAY_STORE_URL = 'https://play.google.com/store';

/**
 * Landing page for technician-only logins. The CRM is not for them: their work
 * (jobs, quotes, photos, signatures) lives in the Steddy app. ProtectedRoute
 * sends every tech-only user here regardless of the URL they opened.
 */
export default function DownloadApp() {
  const { signOut, user } = useAuth();
  const { company } = useTenant();
  const providers = user?.app_metadata?.providers;
  const usesGoogle = Array.isArray(providers) && providers.includes('google');

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background to-secondary/20 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10">
            <Smartphone className="h-7 w-7 text-primary" />
          </div>
          <CardTitle>Sign in on the Steddy app</CardTitle>
          <CardDescription>
            {company?.name ? `${company.name} runs its field work` : 'Field work runs'} through the Steddy app on
            your phone. Your jobs, quotes, photos and customer signatures all live there; this website is the office
            side.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-2">
            <Button asChild variant="outline" className="justify-start gap-3">
              <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
                <Apple className="h-4 w-4" />
                Download on the App Store
              </a>
            </Button>
            <Button asChild variant="outline" className="justify-start gap-3">
              <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
                <Play className="h-4 w-4" />
                Get it on Google Play
              </a>
            </Button>
          </div>

          <div className="rounded-lg bg-muted/50 p-3 text-sm text-muted-foreground">
            {usesGoogle ? (
              <>
                Tap <span className="font-medium text-foreground">Continue with Google</span> and choose{' '}
                <span className="font-medium text-foreground">{user?.email}</span>.
              </>
            ) : (
              <>
                Sign in with <span className="font-medium text-foreground">{user?.email}</span> and the password you
                just created.
              </>
            )}{' '}
            If you were expecting office access, ask your administrator to add an office role to your account.
          </div>

          <Button variant="ghost" className="w-full gap-2" onClick={() => void signOut()}>
            <LogOut className="h-4 w-4" />
            Sign out
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
